/**
 * The FutureMoney web design system tokens, sourced from
 * design/futuremoney-home-spec.html (dark) with a derived light mode.
 * Dark values are literal from the spec – do not round them. Light values
 * mirror the same roles on a warm paper ground: the filled button inverts
 * (ink on paper), copper deepens for contrast, translucent cards go white.
 *
 * Both the app themes (darkTheme/lightTheme) and the home screen consume
 * these, so the two stay in lockstep.
 */

export type HomeTokenSet = Record<keyof typeof homeDark, string>;

export const homeDark = {
  ground: "#0E121A",
  ink: "#F2EFE9",
  copper: "#E09B7C",
  copperHover: "#F0B296",
  copperText: "#E7B79E",
  copperDeep: "#B4674C",
  sage: "#9DCBBB",
  sageDeep: "#4E8F7B",

  ink80: "rgba(242,239,233,0.80)",
  ink72: "rgba(242,239,233,0.72)",
  ink66: "rgba(242,239,233,0.66)",
  ink62: "rgba(242,239,233,0.62)",
  ink58: "rgba(242,239,233,0.58)",
  ink52: "rgba(242,239,233,0.52)",
  ink50: "rgba(242,239,233,0.50)",
  ink48: "rgba(242,239,233,0.48)",
  ink46: "rgba(242,239,233,0.46)",
  ink44: "rgba(242,239,233,0.44)",
  ink42: "rgba(242,239,233,0.42)",
  ink38: "rgba(242,239,233,0.38)",
  ink34: "rgba(242,239,233,0.34)",
  ink32: "rgba(242,239,233,0.32)",

  line05: "rgba(242,239,233,0.05)",
  line08: "rgba(242,239,233,0.08)",
  line10: "rgba(242,239,233,0.10)",
  line12: "rgba(242,239,233,0.12)",
  line14: "rgba(242,239,233,0.14)",
  line18: "rgba(242,239,233,0.18)",
  line20: "rgba(242,239,233,0.20)",
  line26: "rgba(242,239,233,0.26)",

  fill04: "rgba(242,239,233,0.04)",
  fill06: "rgba(242,239,233,0.06)",
  fill07: "rgba(242,239,233,0.07)",
  fill08: "rgba(242,239,233,0.08)",
  fill09: "rgba(242,239,233,0.09)",
  fill10: "rgba(242,239,233,0.10)",
  fill14: "rgba(242,239,233,0.14)",
  fill25: "rgba(242,239,233,0.25)",

  mesh: `radial-gradient(1250px 760px at 93% -8%, rgba(122,177,160,0.32), transparent 62%),
    radial-gradient(1050px 860px at -8% 22%, rgba(196,120,92,0.26), transparent 62%),
    radial-gradient(980px 660px at 56% 30%, rgba(58,74,104,0.32), transparent 70%),
    radial-gradient(1150px 900px at 104% 62%, rgba(196,120,92,0.20), transparent 64%),
    radial-gradient(1000px 820px at -4% 84%, rgba(122,177,160,0.16), transparent 66%),
    radial-gradient(900px 700px at 62% 100%, rgba(78,86,124,0.22), transparent 68%),
    #0E121A`,

  cardBg: "rgba(20,26,40,0.68)",
  cardSolid: "#151B29",
  projectionBg: "linear-gradient(158deg, rgba(26,34,52,0.80) 0%, rgba(18,23,36,0.86) 100%)",
  valueBg:
    "linear-gradient(150deg, rgba(143,191,176,0.20) 0%, rgba(22,28,44,0.74) 62%), rgba(20,26,40,0.60)",
  promoBorder: "rgba(224,155,124,0.30)",
  promoBg: "linear-gradient(140deg, rgba(224,155,124,0.20) 0%, rgba(224,155,124,0.07) 100%)",
  sageChipBg: "rgba(143,191,176,0.16)",
  sageFill: "rgba(122,177,160,0.20)",
  copperFill: "rgba(224,155,124,0.22)",
  avatarCopperBg: "rgba(224,155,124,0.22)",
  avatarCopperBorder: "rgba(224,155,124,0.45)",
  avatarSageBg: "rgba(143,191,176,0.22)",
  avatarSageBorder: "rgba(143,191,176,0.42)",

  navBg: "rgba(14,18,26,0.72)",
  grainOpacity: "0.15",
  error: "#D98A80",
  errorStrong: "#8E3B33",
};

export const homeLight: HomeTokenSet = {
  ground: "#F5F1E9",
  ink: "#1F2530",
  copper: "#B4674C",
  copperHover: "#A05A41",
  copperText: "#9C5237",
  copperDeep: "#8F4A33",
  sage: "#3E7A66",
  sageDeep: "#2F6152",

  ink80: "rgba(31,37,48,0.80)",
  ink72: "rgba(31,37,48,0.72)",
  ink66: "rgba(31,37,48,0.66)",
  ink62: "rgba(31,37,48,0.62)",
  ink58: "rgba(31,37,48,0.58)",
  ink52: "rgba(31,37,48,0.52)",
  ink50: "rgba(31,37,48,0.50)",
  ink48: "rgba(31,37,48,0.48)",
  ink46: "rgba(31,37,48,0.46)",
  ink44: "rgba(31,37,48,0.44)",
  ink42: "rgba(31,37,48,0.42)",
  ink38: "rgba(31,37,48,0.38)",
  ink34: "rgba(31,37,48,0.34)",
  ink32: "rgba(31,37,48,0.32)",

  line05: "rgba(31,37,48,0.05)",
  line08: "rgba(31,37,48,0.08)",
  line10: "rgba(31,37,48,0.10)",
  line12: "rgba(31,37,48,0.12)",
  line14: "rgba(31,37,48,0.14)",
  line18: "rgba(31,37,48,0.18)",
  line20: "rgba(31,37,48,0.20)",
  line26: "rgba(31,37,48,0.26)",

  fill04: "rgba(31,37,48,0.04)",
  fill06: "rgba(31,37,48,0.06)",
  fill07: "rgba(31,37,48,0.07)",
  fill08: "rgba(31,37,48,0.08)",
  fill09: "rgba(31,37,48,0.09)",
  fill10: "rgba(31,37,48,0.10)",
  fill14: "rgba(31,37,48,0.14)",
  fill25: "rgba(31,37,48,0.25)",

  mesh: `radial-gradient(1250px 760px at 93% -8%, rgba(122,177,160,0.22), transparent 62%),
    radial-gradient(1050px 860px at -8% 22%, rgba(224,155,124,0.20), transparent 62%),
    radial-gradient(980px 660px at 56% 30%, rgba(151,179,208,0.16), transparent 70%),
    radial-gradient(1150px 900px at 104% 62%, rgba(224,155,124,0.14), transparent 64%),
    radial-gradient(1000px 820px at -4% 84%, rgba(122,177,160,0.12), transparent 66%),
    radial-gradient(900px 700px at 62% 100%, rgba(126,112,168,0.10), transparent 68%),
    #F5F1E9`,

  cardBg: "rgba(255,255,255,0.72)",
  cardSolid: "#FFFFFF",
  projectionBg: "linear-gradient(158deg, rgba(255,255,255,0.86) 0%, rgba(250,247,240,0.90) 100%)",
  valueBg:
    "linear-gradient(150deg, rgba(143,191,176,0.28) 0%, rgba(255,255,255,0.80) 62%), rgba(255,255,255,0.66)",
  promoBorder: "rgba(180,103,76,0.34)",
  promoBg: "linear-gradient(140deg, rgba(224,155,124,0.26) 0%, rgba(224,155,124,0.10) 100%)",
  sageChipBg: "rgba(143,191,176,0.28)",
  sageFill: "rgba(122,177,160,0.26)",
  copperFill: "rgba(224,155,124,0.26)",
  avatarCopperBg: "rgba(224,155,124,0.30)",
  avatarCopperBorder: "rgba(180,103,76,0.50)",
  avatarSageBg: "rgba(143,191,176,0.32)",
  avatarSageBorder: "rgba(62,122,102,0.42)",

  navBg: "rgba(245,241,233,0.80)",
  grainOpacity: "0.10",
  error: "#B3564C",
  errorStrong: "#8E3B33",
};

export const homeShared = {
  serif: `"Playfair Display", Georgia, serif`,
  sans: `Inter, "Helvetica Neue", Arial, sans-serif`,
  radius: 18,
  pagePad: "min(80px, 5.5vw)",
} as const;
